import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { ShoppingBag, Package, DollarSign, TrendingUp, Eye, Edit, Trash2, Plus, X, MapPin, Mail, Phone, User, Star, Settings, Truck } from 'lucide-react';
import ExportButtons from './ExportButtons';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ShopManager = () => {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalProducts: 0, totalOrders: 0, revenue: 0, inStock: 0 });
  const [activeTab, setActiveTab] = useState('products');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [isCreatingProduct, setIsCreatingProduct] = useState(false); // New state for create mode
  const [editForm, setEditForm] = useState({
    title: '',
    price: '',
    image_url: '',
    stock: '',
    status: 'active',
    description: '' // Added description field
  });
  const [productReviews, setProductReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  
  // Image upload states
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  
  // Shop settings states
  const [shopSettings, setShopSettings] = useState({
    shipping_free_threshold: 149.0,
    shipping_flat_rate: 10.0,
    tax_rate: 0.13,
    currency: 'CAD'
  });
  const [settingsLoading, setSettingsLoading] = useState(false);

  useEffect(() => {
    fetchData();
    if (activeTab === 'reviews') {
      fetchProductReviews();
    }
    if (activeTab === 'settings') {
      fetchShopSettings();
    }
  }, [activeTab]);

  const fetchShopSettings = async () => {
    try {
      setSettingsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/admin/shop/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShopSettings(response.data);
    } catch (error) {
      console.error('Error fetching shop settings:', error);
      toast({ title: 'Error', description: 'Failed to fetch shop settings', variant: 'destructive' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const updateShopSettings = async () => {
    try {
      setSettingsLoading(true);
      const token = localStorage.getItem('token');
      await axios.put(`${BACKEND_URL}/api/admin/shop/settings`, shopSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Shop settings updated successfully' });
    } catch (error) {
      console.error('Error updating shop settings:', error);
      toast({ title: 'Error', description: 'Failed to update shop settings', variant: 'destructive' });
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Use admin API for products to avoid cache issues
      const [productsRes, ordersRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/shop/products`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/admin/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: { orders: [] } }))
      ]);
      
      setProducts(productsRes.data.products || []);
      setOrders(ordersRes.data.orders || []);
      
      const allProducts = productsRes.data.products || [];
      const allOrders = ordersRes.data.orders || [];
      
      // Calculate revenue from delivered orders only
      const revenue = allOrders
        .filter(order => order.order_status === 'delivered')
        .reduce((sum, order) => sum + (parseFloat(order.total) || 0), 0);
      
      // Count in-stock products (products with inventory_qty > 0 or in_stock = true)
      const inStockCount = allProducts.filter(p => {
        const variant = p.variants && p.variants[0];
        const qty = variant?.inventory_qty || p.stock || 0;
        return p.in_stock || qty > 0;
      }).length;
      
      setStats({
        totalProducts: allProducts.length,
        totalOrders: allOrders.length,
        revenue: revenue,
        inStock: inStockCount
      });
    } catch (error) {
      console.error('Error fetching shop data:', error);
      toast({
        title: "Error",
        description: "Failed to fetch shop data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/admin/shop/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Invalidate cache
      try {
        await axios.post(`${BACKEND_URL}/api/admin/shop/cache/invalidate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (cacheError) {
        console.log('Cache invalidation skipped:', cacheError);
      }
      
      toast({
        title: "Success",
        description: "Product deleted successfully",
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Error",
        description: "Failed to delete product",
        variant: "destructive"
      });
    }
  };

  // Remove image handler
  const handleRemoveImage = () => {
    const updatedForm = {
      title: editForm.title,
      price: editForm.price,
      image_url: '',  // Clear image URL
      stock: editForm.stock,
      status: editForm.status,
      description: editForm.description  // Preserve description
    };
    setEditForm(updatedForm);
    setImagePreview('');
    
    toast({
      title: "Image Removed",
      description: "Image has been removed. Click Save Changes to update.",
    });
  };

  // Image upload handlers
  const handleImageFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: "Invalid File Type",
          description: "Please select a valid image file (JPG, PNG, GIF, WEBP)",
          variant: "destructive"
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "File size must be less than 5MB",
          variant: "destructive"
        });
        return;
      }

      // Create local preview immediately
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);

      // Automatically upload the file
      setUploadingImage(true);
      
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          const newImageUrl = data.url;
          
          console.log('Upload successful, URL:', newImageUrl);
          
          // Update form with uploaded image URL (store relative path in DB)
          const updatedForm = {
            title: editForm.title,
            price: editForm.price,
            image_url: newImageUrl,  // Store server path: /uploads/xxx
            stock: editForm.stock,
            status: editForm.status,
            description: editForm.description  // Preserve description
          };
          
          setEditForm(updatedForm);
          // DON'T overwrite imagePreview! Keep the FileReader data URL for preview
          // setImagePreview(newImageUrl); // ❌ REMOVED - This was causing the white box!
          
          toast({
            title: "Success",
            description: "Image uploaded successfully!",
          });
        } else {
          const errorData = await response.json();
          toast({
            title: "Upload Failed",
            description: errorData.detail || "Failed to upload image",
            variant: "destructive"
          });
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast({
          title: "Error",
          description: "Error uploading image",
          variant: "destructive"
        });
      } finally {
        setUploadingImage(false);
      }
    }
  };

  const handleUploadImage = async () => {
    if (!selectedImageFile) {
      toast({
        title: "No File Selected",
        description: "Please select an image first",
        variant: "destructive"
      });
      return;
    }

    setUploadingImage(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', selectedImageFile);

      const response = await fetch(`${BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        const newImageUrl = data.url;
        
        console.log('Upload successful, URL:', newImageUrl);
        console.log('Current editForm before update:', editForm);
        
        // Force update by creating completely new object
        const updatedForm = {
          title: editForm.title,
          price: editForm.price,
          image_url: newImageUrl,  // New URL here
          stock: editForm.stock,
          status: editForm.status,
          description: editForm.description  // Preserve description
        };
        
        console.log('New editForm:', updatedForm);
        setEditForm(updatedForm);
        // Set preview with full URL for display - handle different formats
        let previewPath = newImageUrl;
        if (newImageUrl.startsWith('/uploads/')) {
          previewPath = newImageUrl.replace('/uploads/', '/api/uploads/general/');
        } else if (!newImageUrl.startsWith('/api/')) {
          previewPath = newImageUrl;  // Keep as-is if not a known format
        }
        setImagePreview(`${BACKEND_URL}${previewPath}`);
        setSelectedImageFile(null);
        
        toast({
          title: "Success",
          description: "Image uploaded successfully!",
        });
      } else {
        const errorData = await response.json();
        toast({
          title: "Upload Failed",
          description: errorData.detail || "Failed to upload image",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: "Error",
        description: "Error uploading image",
        variant: "destructive"
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const openEditModal = (product) => {
    setSelectedProduct(product);
    setIsCreatingProduct(false);
    // Get first variant or default values
    const variant = product.variants && product.variants[0];
    const imgUrl = (product.media && product.media[0]) || product.image_url || '';
    
    // Get description from either description or description_html field
    const productDescription = product.description || product.description_html || '';
    console.log('[EDIT MODAL] Product description:', productDescription);
    
    setEditForm({
      title: product.title || '',
      price: variant?.price || product.price || '',
      image_url: imgUrl,  // Store relative path
      stock: variant?.inventory_qty || product.stock || 0,
      status: product.status || 'active',
      description: productDescription
    });
    
    // For preview: Convert relative path to full backend URL
    // Images are served from backend uploads directory
    let previewUrl = '';
    if (imgUrl) {
      if (imgUrl.startsWith('http')) {
        previewUrl = imgUrl;  // Already full URL
      } else if (imgUrl.startsWith('data:')) {
        previewUrl = imgUrl;  // FileReader data URL
      } else {
        // Handle different URL formats for Kubernetes routing
        let cleanPath = imgUrl;
        if (imgUrl.startsWith('/uploads/')) {
          // Legacy format: /uploads/xxx.jpg -> /api/uploads/general/xxx.jpg
          cleanPath = imgUrl.replace('/uploads/', '/api/uploads/general/');
        } else if (!imgUrl.startsWith('/api/')) {
          // Unknown format: prepend /api
          cleanPath = `/api${imgUrl}`;
        }
        // If already starts with /api/, use as-is
        previewUrl = `${BACKEND_URL}${cleanPath}`;
      }
    }
    
    console.log('[EDIT MODAL] Setting preview URL:', previewUrl);
    setImagePreview(previewUrl);
    setSelectedImageFile(null);
    setShowProductModal(true);
  };

  // Open Create Product Modal
  const openCreateModal = () => {
    setSelectedProduct(null);
    setIsCreatingProduct(true);
    setEditForm({
      title: '',
      price: '',
      image_url: '',
      stock: '',
      status: 'active',
      description: ''
    });
    setImagePreview('');
    setSelectedImageFile(null);
    setShowProductModal(true);
  };

  const closeEditModal = () => {
    setShowProductModal(false);
    setSelectedProduct(null);
    setIsCreatingProduct(false);
    setEditForm({
      title: '',
      price: '',
      image_url: '',
      stock: '',
      status: 'active',
      description: ''
    });
    setImagePreview('');
    setSelectedImageFile(null);
  };

  const updateProduct = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      console.log('=== UPDATE PRODUCT DEBUG ===');
      console.log('editForm:', editForm);
      console.log('editForm.description:', editForm.description);
      console.log('editForm.description length:', editForm.description?.length);
      
      // Prepare update payload - ensure description is included
      const updateData = {
        title: editForm.title,
        status: editForm.status,
        description: editForm.description || '',
        description_html: editForm.description || '', // Also send as description_html
        media: editForm.image_url ? [editForm.image_url] : [],
        variants: [{
          price: parseFloat(editForm.price),
          inventory_qty: parseInt(editForm.stock),
          inventory_policy: 'deny'
        }]
      };
      
      console.log('Update payload:', updateData);
      console.log('Payload description:', updateData.description);

      await axios.put(
        `${BACKEND_URL}/api/admin/shop/products/${selectedProduct._id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Invalidate cache
      try {
        await axios.post(`${BACKEND_URL}/api/admin/shop/cache/invalidate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (cacheError) {
        console.log('Cache invalidation skipped:', cacheError);
      }

      toast({
        title: "Success",
        description: "Product updated successfully",
      });
      
      closeEditModal();
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error updating product:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to update product",
        variant: "destructive"
      });
    }
  };

  // Create new product
  const createProduct = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const createData = {
        title: editForm.title,
        description_html: editForm.description || '', // Backend expects description_html
        status: editForm.status || 'active',
        media: editForm.image_url ? [editForm.image_url] : [],
        variants: [{
          price: parseFloat(editForm.price) || 0,
          inventory_qty: parseInt(editForm.stock) || 0,
          inventory_policy: 'deny'
        }]
      };

      console.log('Creating product with data:', createData);

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/shop/products`,
        createData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Product created:', response.data);

      // Invalidate cache to ensure fresh data
      try {
        await axios.post(`${BACKEND_URL}/api/admin/shop/cache/invalidate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (cacheError) {
        console.log('Cache invalidation skipped:', cacheError);
      }

      toast({
        title: "Success",
        description: "Product created successfully",
      });
      
      closeEditModal();
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error creating product:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create product",
        variant: "destructive"
      });
    }
  };

  // Handle form submission (create or update)
  const handleProductSubmit = (e) => {
    if (isCreatingProduct) {
      createProduct(e);
    } else {
      updateProduct(e);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      console.log(`Updating order ${orderId} to status: ${newStatus}`);
      const response = await axios.patch(`${BACKEND_URL}/api/orders/${orderId}/status`, 
        { order_status: newStatus },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      console.log('Update response:', response.data);
      
      toast({
        title: "Başarılı",
        description: `Sipariş durumu güncellendi: ${newStatus}`,
      });
      
      fetchData();
    } catch (error) {
      console.error('Error updating order status:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: "Hata",
        description: error.response?.data?.detail || "Sipariş durumu güncellenemedi",
        variant: "destructive"
      });
    }
  };

  // Dedicated Ship endpoint - uses state machine validation
  const shipOrder = async (orderId) => {
    try {
      console.log(`Shipping order ${orderId}`);
      const response = await axios.post(`${BACKEND_URL}/api/admin/orders/${orderId}/ship`, 
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      console.log('Ship response:', response.data);
      
      toast({
        title: "Başarılı",
        description: "Sipariş kargoya verildi. Müşteriye email gönderildi.",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error shipping order:', error);
      toast({
        title: "Hata",
        description: error.response?.data?.detail || "Sipariş kargoya verilemedi",
        variant: "destructive"
      });
    }
  };

  // Dedicated Deliver endpoint - uses state machine validation
  const deliverOrder = async (orderId) => {
    try {
      console.log(`Delivering order ${orderId}`);
      const response = await axios.post(`${BACKEND_URL}/api/admin/orders/${orderId}/deliver`, 
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      console.log('Deliver response:', response.data);
      
      toast({
        title: "Başarılı",
        description: "Sipariş teslim edildi. Müşteriye email gönderildi.",
      });
      
      fetchData();
    } catch (error) {
      console.error('Error delivering order:', error);
      toast({
        title: "Hata",
        description: error.response?.data?.detail || "Sipariş teslim edilemedi",
        variant: "destructive"
      });
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowOrderModal(true);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price || 0);
  };

  const fetchProductReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/shop/reviews?page=1&limit=50`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setProductReviews(response.data.reviews || []);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleReviewAction = async (reviewId, status) => {
    try {
      await axios.put(
        `${BACKEND_URL}/api/admin/shop/reviews/${reviewId}`,
        { status },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast({
        title: "Success",
        description: `Review ${status} successfully`,
      });
      fetchProductReviews();
    } catch (error) {
      console.error('Error updating review:', error);
      toast({
        title: "Error",
        description: "Failed to update review",
        variant: "destructive"
      });
    }
  };

  const handleDeleteReview = async (reviewId, productTitle) => {
    // Confirm before deleting
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this review for "${productTitle}"? This action cannot be undone.`
    );
    
    if (!confirmDelete) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/admin/shop/reviews/${reviewId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast({
        title: "Success",
        description: "Review deleted successfully",
      });
      fetchProductReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      toast({
        title: "Error",
        description: "Failed to delete review",
        variant: "destructive"
      });
    }
  };


  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300';
      case 'processing': return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300';
      case 'shipped': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      case 'delivered': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300';
      case 'cancelled': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getCustomerName = (order) => {
    if (order.shipping_address) {
      return `${order.shipping_address.first_name} ${order.shipping_address.last_name}`;
    }
    return order.guest_email || order.user_id?.substring(0, 8) || 'Unknown';
  };

  const getCustomerEmail = (order) => {
    return order.guest_email || 'N/A';
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800 dark:text-white mb-2">Shop Management</h2>
        <p className="text-gray-600 dark:text-gray-400">Manage products, orders, and inventory</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Products</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.totalProducts}</p>
            </div>
            <Package className="w-8 h-8 text-purple-500" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total Orders</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.totalOrders}</p>
            </div>
            <ShoppingBag className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Revenue (Delivered)</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{formatPrice(stats.revenue)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-500" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">In Stock</p>
              <p className="text-2xl font-bold text-gray-800 dark:text-white">{stats.inStock}</p>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'products'
                ? 'border-b-2 border-purple-600 text-red-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Products ({stats.totalProducts})
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'orders'
                ? 'border-b-2 border-purple-600 text-red-600'
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Orders ({stats.totalOrders})
          </button>
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-6 py-3 font-medium transition-colors ${
              activeTab === 'reviews'
                ? 'border-b-2 border-purple-600 text-red-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
            }`}
          >
            Product Reviews
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'settings'
                ? 'border-b-2 border-purple-600 text-red-600'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800'
            }`}
          >
            <Settings className="w-4 h-4" />
            Settings
          </button>
        </div>
      </div>

      {/* Content */}
      {/* Products Section */}
      {activeTab === 'products' && (
        <>
          {/* Header with Add Button and Export */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <button
              onClick={openCreateModal}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md font-semibold"
            >
              <Plus className="w-5 h-5" />
              Add New Product
            </button>
            
            {/* Export Buttons for Products */}
            <ExportButtons 
              data={products}
              columns={[
                { header: 'Product ID', key: 'id', accessor: (row) => row._id || row.id || 'N/A' },
                { header: 'Title', key: 'title', accessor: (row) => row.title || 'N/A' },
                { header: 'Price ($)', key: 'price', accessor: (row) => {
                  const variant = row.variants && row.variants[0];
                  const price = variant?.price || row.price || 0;
                  return price.toFixed(2);
                }},
                { header: 'Stock', key: 'stock', accessor: (row) => {
                  const variant = row.variants && row.variants[0];
                  return variant?.inventory_qty || row.stock || 0;
                }},
                { header: 'Status', key: 'status', accessor: (row) => row.in_stock ? 'In Stock' : 'Out of Stock' }
              ]}
              filename="shop-products"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-3 p-8 text-center text-gray-500 dark:text-gray-400">Loading products...</div>
            ) : products.length === 0 ? (
              <div className="col-span-3 p-8 text-center text-gray-500 dark:text-gray-400">No products found</div>
            ) : (
              products.map((product) => (
              <div key={product._id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-t-lg overflow-hidden aspect-square">
                  <img
                    src={(() => {
                      const mediaPath = (product.media && product.media[0]) || product.image_url;
                      if (mediaPath && !mediaPath.startsWith('http') && mediaPath !== '/product-placeholder.jpg') {
                        // Handle different URL formats for Kubernetes routing
                        let cleanPath = mediaPath;
                        if (mediaPath.startsWith('/uploads/')) {
                          // Legacy format: /uploads/xxx.jpg -> /api/uploads/general/xxx.jpg
                          cleanPath = mediaPath.replace('/uploads/', '/api/uploads/general/');
                        } else if (!mediaPath.startsWith('/api/')) {
                          // Unknown format: prepend /api
                          cleanPath = `/api${mediaPath}`;
                        }
                        // If already starts with /api/, use as-is
                        return `${BACKEND_URL}${cleanPath}`;
                      }
                      return mediaPath || '/product-placeholder.jpg';
                    })()}
                    alt={product.title}
                    className={`w-full h-full ${
                      (!product.media || product.media.length === 0) && !product.image_url
                        ? 'object-contain p-4'
                        : 'object-cover'
                    }`}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/product-placeholder.jpg';
                      e.target.className = e.target.className.replace('object-cover', 'object-contain p-4');
                    }}
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-gray-800 dark:text-white mb-2">{product.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-3 line-clamp-2">{product.description}</p>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-2xl font-bold text-red-600">
                      {(() => {
                        const variant = product.variants && product.variants[0];
                        const price = variant?.price || product.price || 0;
                        return formatPrice(price);
                      })()}
                    </span>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      product.in_stock ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                    }`}>
                      {product.in_stock ? 'In Stock' : 'Out of Stock'}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    <span className="font-medium">Stock: </span>
                    <span className="font-semibold dark:text-white">
                      {(() => {
                        const variant = product.variants && product.variants[0];
                        return variant?.inventory_qty || product.stock || 0;
                      })()}
                    </span> units
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href={`/shop/products/${product.slug || product._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </a>
                    <button
                      onClick={() => openEditModal(product)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => deleteProduct(product._id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </>
      )}

      {/* Reviews Section */}
      {activeTab === 'reviews' && (
        <>
          {/* Export Buttons for Reviews */}
          <ExportButtons 
            data={productReviews}
            columns={[
              { header: 'Review ID', key: 'id', accessor: (row) => row.id || 'N/A' },
              { header: 'User Name', key: 'user_name', accessor: (row) => row.user_name || 'N/A' },
              { header: 'Product', key: 'product_title', accessor: (row) => row.product_title || 'Unknown Product' },
              { header: 'Rating', key: 'rating', accessor: (row) => `${row.rating}/5` },
              { header: 'Comment', key: 'comment', accessor: (row) => row.comment || 'No comment' },
              { header: 'Status', key: 'status', accessor: (row) => row.status || 'N/A' },
              { header: 'Order Number', key: 'order_number', accessor: (row) => row.order_number || 'N/A' },
              { header: 'Date', key: 'created_at', accessor: (row) => new Date(row.created_at).toLocaleDateString() }
            ]}
            filename="product-reviews"
          />
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            {reviewsLoading ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading reviews...</div>
            ) : productReviews.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">No product reviews found</div>
            ) : (
            <div className="divide-y">
              {productReviews.map((review) => (
                <div key={review.id} className="p-6 hover:bg-gray-50 dark:bg-gray-700 transition-colors">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-gray-800 dark:text-white">{review.user_name}</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          review.status === 'pending' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          review.status === 'approved' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                          'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                        }`}>
                          {review.status}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{review.product_title || 'Unknown Product'}</p>
                      <p className="text-gray-700 dark:text-gray-300">{review.comment}</p>
                      
                      {/* Review Images */}
                      {review.images && review.images.length > 0 && (
                        <div className="mt-3 flex gap-2 flex-wrap">
                          {review.images.map((imageUrl, idx) => {
                            // Handle relative paths from uploads
                            const fullImageUrl = imageUrl.startsWith('http') 
                              ? imageUrl 
                              : `${BACKEND_URL}${imageUrl}`;
                            
                            return (
                              <img
                                key={idx}
                                src={fullImageUrl}
                                alt={`Review image ${idx + 1}`}
                                className="w-20 h-20 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-700 cursor-pointer hover:border-purple-400 transition-colors"
                                onClick={() => window.open(fullImageUrl, '_blank')}
                              />
                            );
                          })}
                        </div>
                      )}
                      
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                        Order: #{review.order_number} • {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4">
                    {review.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleReviewAction(review.id, 'approved')}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => handleReviewAction(review.id, 'rejected')}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                        >
                          Reject
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteReview(review.id, review.product_title)}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center gap-2"
                      title="Delete this review permanently"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Orders Section */}
      {activeTab === 'orders' && (
        <>
          {/* Export Buttons for Orders */}
          <ExportButtons 
            data={orders}
            columns={[
              { header: 'Order Number', key: 'order_number', accessor: (row) => row.order_number || 'N/A' },
              { header: 'Customer Name', key: 'customer_name', accessor: (row) => getCustomerName(row) },
              { header: 'Email', key: 'email', accessor: (row) => getCustomerEmail(row) },
              { header: 'Total Items', key: 'items', accessor: (row) => row.items?.length || 0 },
              { header: 'Total ($)', key: 'total', accessor: (row) => row.total ? row.total.toFixed(2) : '0.00' },
              { header: 'Status', key: 'order_status', accessor: (row) => row.order_status || 'N/A' },
              { header: 'Date', key: 'created_at', accessor: (row) => formatDate(row.created_at) }
            ]}
            filename="shop-orders"
          />
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            {loading ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">Loading orders...</div>
            ) : orders.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">No orders found</div>
            ) : (
            <div className="divide-y">
              {orders.map((order) => (
                <div key={order.id} className="p-6 hover:bg-gray-50 dark:bg-gray-700 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-semibold text-gray-800 dark:text-white">Order #{order.order_number}</span>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getOrderStatusColor(order.order_status)}`}>
                          {order.order_status}
                        </span>
                      </div>
                      
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-3 space-y-1">
                        <p className="flex items-center gap-2">
                          <User className="w-4 h-4" />
                          <strong>Customer:</strong> {getCustomerName(order)}
                        </p>
                        <p className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <strong>Email:</strong> {getCustomerEmail(order)}
                        </p>
                        <p><strong>Items:</strong> {order.items?.length || 0}</p>
                        <p><strong>Date:</strong> {formatDate(order.created_at)}</p>
                      </div>
                      
                      <div className="text-lg font-bold text-red-600">
                        Total: {formatPrice(order.total)}
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-2 ml-4">
                      <button
                        onClick={() => viewOrderDetails(order)}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm flex items-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                      
                      {order.order_status === 'pending' && (
                        <button
                          onClick={() => updateOrderStatus(order.id, 'processing')}
                          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                        >
                          Process
                        </button>
                      )}
                      {(order.order_status === 'processing' || order.order_status === 'paid') && (
                        <button
                          onClick={() => shipOrder(order.id)}
                          className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                          data-testid="ship-order-btn"
                        >
                          Ship
                        </button>
                      )}
                      {order.order_status === 'shipped' && (
                        <button
                          onClick={() => deliverOrder(order.id)}
                          className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
                          data-testid="deliver-order-btn"
                        >
                          Deliver
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Product Edit/Create Modal */}
      {showProductModal && (selectedProduct || isCreatingProduct) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-red-600 to-black text-white px-6 py-4 flex items-center justify-between rounded-t-lg">
              <h3 className="text-2xl font-bold text-white">{isCreatingProduct ? 'Add New Product' : 'Edit Product'}</h3>
              <button
                onClick={closeEditModal}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleProductSubmit} className="p-6 space-y-6">
              {/* Product Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Product Title *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="Enter product title"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Description
                </label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent min-h-[80px]"
                  placeholder="Enter product description"
                  rows={3}
                />
              </div>

              {/* Price */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Price ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={editForm.price}
                  onChange={(e) => setEditForm({ ...editForm, price: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="0.00"
                  required
                />
              </div>

              {/* Product Image Upload */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Product Image
                </label>
                
                {/* File Input with Auto-Upload */}
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageFileSelect}
                    disabled={uploadingImage}
                    className="w-full text-sm text-gray-600 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-red-600 file:text-white hover:file:bg-red-700 file:cursor-pointer cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  />
                  
                  {/* Uploading Indicator */}
                  {uploadingImage && (
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Uploading image...</span>
                    </div>
                  )}
                  
                  {/* Image Preview with Remove Button */}
                  {(imagePreview || editForm.image_url) && !uploadingImage && (
                    <div className="mt-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Preview:</p>
                      <div className="relative inline-block">
                        <div className="w-32 h-32 bg-gray-100 rounded-lg border-2 border-gray-300 dark:border-gray-600 overflow-hidden">
                          <img
                            src={imagePreview || editForm.image_url || '/product-placeholder.jpeg'}
                            alt="Product Preview"
                            className="w-full h-full object-cover"
                            onLoad={(e) => {
                              console.log('✅ Image loaded successfully:', e.target.src);
                              e.target.style.display = 'block';
                            }}
                            onError={(e) => {
                              console.error('❌ Image failed to load:', e.target.src);
                              e.target.src = '/product-placeholder.jpeg';
                            }}
                            style={{ display: 'block' }}
                          />
                        </div>
                        {/* Remove Image Button */}
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="absolute -top-2 -right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1.5 shadow-lg transition-all hover:scale-110"
                          title="Remove image"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                      {/* Debug Info */}
                      {imagePreview && (
                        <p className="text-xs text-blue-600 mt-1 break-all">
                          URL: {imagePreview.substring(0, 50)}...
                        </p>
                      )}
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Supported formats: JPG, PNG, GIF, WEBP (Max 5MB)
                    <br />
                    Image will upload automatically when selected
                  </p>
                </div>
              </div>

              {/* Stock Quantity */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Stock Quantity *
                </label>
                <input
                  type="number"
                  value={editForm.stock}
                  onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  placeholder="0"
                  min="0"
                  required
                />
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Product will be marked as "Out of Stock" when quantity is 0
                </p>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="active">Active</option>
                  <option value="draft">Draft</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="flex-1 px-6 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-2 bg-gradient-to-r from-red-600 to-black text-white rounded-lg hover:from-red-700 hover:to-gray-900 transition-all shadow-md"
                >
                  {isCreatingProduct ? 'Create Product' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Order Details Modal */}
      {showOrderModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white">Order Details</h3>
              <button
                onClick={() => setShowOrderModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Order Info */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Order Number</p>
                    <p className="font-semibold dark:text-white">{selectedOrder.order_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Status</p>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${getOrderStatusColor(selectedOrder.order_status)}`}>
                      {selectedOrder.order_status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Order Date</p>
                    <p className="font-semibold dark:text-white">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Payment Method</p>
                    <p className="font-semibold capitalize dark:text-white">{selectedOrder.payment_method}</p>
                  </div>
                </div>
              </div>

              {/* Customer Info */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800 dark:text-white">
                  <User className="w-5 h-5" />
                  Customer Information
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <p className="mb-2 text-gray-700 dark:text-gray-200"><strong>Name:</strong> {getCustomerName(selectedOrder)}</p>
                  <p className="text-gray-700 dark:text-gray-200"><strong>Email:</strong> {getCustomerEmail(selectedOrder)}</p>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-3 flex items-center gap-2 text-gray-800 dark:text-white">
                  <MapPin className="w-5 h-5" />
                  Shipping Address
                </h4>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-gray-700 dark:text-gray-200">
                  {selectedOrder.shipping_address ? (
                    <>
                      <p>{selectedOrder.shipping_address.first_name} {selectedOrder.shipping_address.last_name}</p>
                      <p>{selectedOrder.shipping_address.address_line1}</p>
                      {selectedOrder.shipping_address.address_line2 && <p>{selectedOrder.shipping_address.address_line2}</p>}
                      <p>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state_province} {selectedOrder.shipping_address.postal_code}</p>
                      <p>{selectedOrder.shipping_address.country}</p>
                      {selectedOrder.shipping_address.phone && (
                        <p className="mt-2 flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          {selectedOrder.shipping_address.phone}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-gray-500 dark:text-gray-400">No shipping address provided</p>
                  )}
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h4 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Order Items</h4>
                <div className="border dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">Product</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">Quantity</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Price</th>
                        <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-200">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y dark:divide-gray-700">
                      {selectedOrder.items?.map((item, idx) => (
                        <tr key={idx} className="bg-white dark:bg-gray-800">
                          <td className="px-4 py-3 text-gray-700 dark:text-gray-200">{item.product_title || item.product_id}</td>
                          <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-200">{item.quantity}</td>
                          <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">{formatPrice(item.price)}</td>
                          <td className="px-4 py-3 text-right font-semibold text-gray-700 dark:text-white">{formatPrice(item.price * item.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-red-50 dark:bg-gray-700 rounded-lg p-4">
                <div className="space-y-2 text-gray-700 dark:text-gray-200">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span>{formatPrice(selectedOrder.subtotal)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Shipping:</span>
                    <span>{formatPrice(selectedOrder.shipping_cost)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Tax:</span>
                    <span>{formatPrice(selectedOrder.tax_amount)}</span>
                  </div>
                  
                  {/* Coupon/Discount Information */}
                  {selectedOrder.coupon_code && selectedOrder.discount_amount > 0 && (
                    <>
                      <div className="flex justify-between text-green-600 font-medium pt-2 border-t">
                        <span className="flex items-center gap-2">
                          <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                            {selectedOrder.coupon_code}
                          </span>
                          Discount Applied
                        </span>
                        <span>- {formatPrice(selectedOrder.discount_amount)}</span>
                      </div>
                      {selectedOrder.coupon_info && (
                        <div className="text-xs text-gray-600 dark:text-gray-400 italic pl-2">
                          {selectedOrder.coupon_info.discount_type === 'percentage' 
                            ? `${selectedOrder.coupon_info.discount_value}% off`
                            : `$${selectedOrder.coupon_info.discount_value} off`
                          }
                        </div>
                      )}
                    </>
                  )}
                  
                  <div className="flex justify-between text-lg font-bold text-red-600 pt-2 border-t">
                    <span>Total:</span>
                    <span>{formatPrice(selectedOrder.total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <div className="max-w-2xl">
            <h3 className="text-xl font-bold text-gray-800 dark:text-white mb-6 flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Shop Settings
            </h3>

            {settingsLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
              </div>
            ) : (
              <div className="space-y-8">
                {/* Shipping Settings */}
                <div className="border dark:border-gray-700 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <Truck className="w-5 h-5 text-blue-500" />
                    Shipping Settings
                  </h4>
                  
                  <div className="space-y-4">
                    {/* Free Shipping Threshold */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Minimum Order Amount for Free Shipping ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={shopSettings.shipping_free_threshold}
                        onChange={(e) => setShopSettings(prev => ({
                          ...prev,
                          shipping_free_threshold: parseFloat(e.target.value) || 0
                        }))}
                        className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 50"
                      />
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Orders at or above this amount qualify for free shipping. Set to 0 to always charge shipping.
                      </p>
                    </div>

                    {/* Flat Rate Shipping */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Flat Rate Shipping Cost ($)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={shopSettings.shipping_flat_rate}
                        onChange={(e) => setShopSettings(prev => ({
                          ...prev,
                          shipping_flat_rate: parseFloat(e.target.value) || 0
                        }))}
                        className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                        placeholder="e.g., 10"
                      />
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        This amount is charged for orders below the free shipping threshold.
                      </p>
                    </div>

                    {/* Preview */}
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mt-4">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Customer will see:
                      </p>
                      <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                        <p>• Orders under ${shopSettings.shipping_free_threshold?.toFixed(2)}: <span className="font-semibold">${shopSettings.shipping_flat_rate?.toFixed(2)} shipping</span></p>
                        <p>• Orders ${shopSettings.shipping_free_threshold?.toFixed(2)}+: <span className="font-semibold text-green-600">FREE shipping</span></p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tax Settings */}
                <div className="border dark:border-gray-700 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    Tax Settings
                  </h4>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1"
                      step="0.01"
                      value={shopSettings.tax_rate}
                      onChange={(e) => setShopSettings(prev => ({
                        ...prev,
                        tax_rate: parseFloat(e.target.value) || 0
                      }))}
                      className="w-full px-4 py-2 border dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-700 dark:text-white"
                      placeholder="e.g., 0.13 for 13%"
                    />
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Enter as decimal (e.g., 0.13 = 13% HST)
                    </p>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end">
                  <button
                    onClick={updateShopSettings}
                    disabled={settingsLoading}
                    className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md font-semibold disabled:opacity-50"
                  >
                    {settingsLoading ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopManager;
